import { useMemo, useState } from 'react';
import { useDebounceEffect } from 'ahooks';
import useTaskSpeedStore from '$/store/useTaskSpeedStore';
import { toolsCountCost } from '$/domains/mcp/mcpService';
import type { NodeCostInfo } from '$/store/useTeamFlowStore';
import type { RoleGenFormValues } from './RoleSettingForm';
import type { RoleGenModelOption } from './useModelOptions';
import { buildRoleGenTaskParameters, ROLE_GEN_COST_ITEM_ID } from './roleGenerateTask';

interface UseRoleGenCostOptions {
  roomId: string;
  values: RoleGenFormValues;
  selectedModel?: RoleGenModelOption;
  enabled: boolean;
}

/** 将 countcost 单项结果归一化为弹框可展示的萝卜费用。 */
function normalizeRoleGenCostItem(item?: { cost?: number; discount_cost?: number; }): NodeCostInfo {
  return {
    cost: item?.cost ?? 0,
    ...(typeof item?.discount_cost === 'number' ? { discount_cost: item.discount_cost } : {}),
  };
}

/** 角色创建弹框萝卜询价，沿用画布 countcost 接口与 task_speed。 */
export default function useRoleGenCost({
  roomId,
  values,
  selectedModel,
  enabled,
}: UseRoleGenCostOptions): {
    cost?: number;
    discountCost?: number;
    loading: boolean;
  } {
  const taskSpeed = useTaskSpeedStore((state) => state.taskSpeed);
  const [costInfo, setCostInfo] = useState<NodeCostInfo | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const costParams = useMemo(() => {
    if (!selectedModel) return undefined;
    return buildRoleGenTaskParameters(values, selectedModel);
  }, [selectedModel, values]);
  const paramsKey = costParams ? JSON.stringify(costParams) : '';

  useDebounceEffect(
    () => {
      let disposed = false;
      if (!enabled || !roomId || !selectedModel?.apiName || !costParams) {
        setCostInfo(undefined);
        setLoading(false);
        return () => {
          disposed = true;
        };
      }

      setCostInfo(undefined);
      setLoading(true);
      toolsCountCost({
        room_id: roomId,
        task_speed: taskSpeed,
        items: [{
          id: ROLE_GEN_COST_ITEM_ID,
          tool_name: selectedModel.apiName,
          params: costParams,
        }],
      })
        .then((result) => {
          if (disposed) return;
          setCostInfo(normalizeRoleGenCostItem(
            result.items.find((item) => item.id === ROLE_GEN_COST_ITEM_ID),
          ));
        })
        .catch(() => {
          if (!disposed) {
            setCostInfo(undefined);
          }
        })
        .finally(() => {
          if (!disposed) {
            setLoading(false);
          }
        });

      return () => {
        disposed = true;
      };
    },
    [enabled, roomId, selectedModel?.apiName, paramsKey, taskSpeed],
    { wait: 300 },
  );

  return {
    cost: costInfo?.cost,
    discountCost: costInfo?.discount_cost,
    loading,
  };
}
